// components/utils_jsx/PrivacyContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const PrivacyContext = createContext({
  hideAmounts: false,
  toggleHideAmounts: () => {},
  setHideAmounts: () => {},
});

const STORAGE_KEY = 'hideAmounts';

export function PrivacyProvider({ children }) {
  const [hideAmounts, setHideAmountsState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(hideAmounts));
    } catch {
      // ignore
    }
  }, [hideAmounts]);

  const setHideAmounts = useCallback((value) => {
    setHideAmountsState(Boolean(value));
  }, []);

  const toggleHideAmounts = useCallback(() => {
    setHideAmountsState((prev) => !prev);
  }, []);

  return (
    <PrivacyContext.Provider value={{ hideAmounts, toggleHideAmounts, setHideAmounts }}>
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacy() {
  const ctx = useContext(PrivacyContext);
  if (!ctx) {
    // fallback si le Provider n'est pas monté
    return {
      hideAmounts: false,
      toggleHideAmounts: () => {},
      setHideAmounts: () => {},
    };
  }
  return ctx;
}

/**
 * Formate un montant en respectant le mode privacy.
 * @param {number|null|undefined} value
 * @param {boolean} hideAmounts
 * @param {object} options - { currency: '€' | '$', decimals: 2, stars: '****' }
 */
export function formatAmount(value, hideAmounts, options = {}) {
  const {
    currency = '€',
    decimals = 2,
    stars = '****',
  } = options;

  if (hideAmounts) {
    return stars;
  }

  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
    return '—';
  }

  const num = Number(value);
  const formatted = num.toLocaleString('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return currency ? `${formatted} ${currency}` : formatted;
}

export default PrivacyContext;
